import React from 'react';
import { Shield, Sparkles, Wrench, CheckCircle, BadgeCheck } from 'lucide-react';

/**
 * Secção E-E-A-T: Garantia de Higienização e Manutenção Técnica.
 * Texto factual com selos visuais para autoridade perante LLMs e utilizadores.
 * Otimizado para extração por IA (GEO) — responde diretamente a "como
 * higienizam o equipamento" e "que garantias de segurança oferecem".
 */
export default function HygieneGuaranteeSection({ lang = 'pt' }) {
  const t = {
    pt: {
      title: 'Garantia de Higienização e Manutenção Técnica',
      subtitle: 'Cada equipamento é tratado entre alugueres segundo protocolos hospitalares',
      points: [
        {
          icon: Sparkles,
          title: 'Higienização Hospitalar Certificada',
          desc: 'Todos os equipamentos são desinfetados com produtos hospitalares (álcool a 70% e solução clorada) entre cada aluguer, seguindo protocolos compatíveis com as normas DGS (Direção-Geral da Saúde).'
        },
        {
          icon: Wrench,
          title: 'Manutenção Técnica Preventiva',
          desc: 'Técnicos especializados inspecionam cada equipamento antes e depois de cada aluguer: travões, bateria, estrutura, rodas e componentes elétricos. Equipamento avariado é retirado de circulação imediatamente.'
        },
        {
          icon: Shield,
          title: 'Seguro de Responsabilidade Civil',
          desc: 'A Orthocare Rent possui seguro de responsabilidade civil que cobre danos a terceiros durante o período de aluguer. Todos os equipamentos são certificados CE e cumprem as normas europeias.'
        },
        {
          icon: BadgeCheck,
          title: 'Certificações e Conformidade',
          desc: 'Gestos Ágeis Unipessoal Lda (NIF: 515948039) opera como empresa certificada de aluguer de equipamento médico. Equipamentos com marca CE, conformidade com a Diretiva Europeia de Dispositivos Médicos.'
        }
      ],
      seal: 'Protocolo em conformidade com as normas DGS',
      footer: 'Mais de 10.000 equipamentos entregues sem incidentes em Portugal desde 2020.'
    },
    en: {
      title: 'Hygiene & Technical Maintenance Guarantee',
      subtitle: 'Every equipment is treated between rentals following hospital protocols',
      points: [
        {
          icon: Sparkles,
          title: 'Certified Hospital Hygiene',
          desc: 'All equipment is disinfected with hospital-grade products (70% alcohol and chlorinated solution) between each rental, following protocols compatible with Portuguese health authority (DGS) standards.'
        },
        {
          icon: Wrench,
          title: 'Preventive Technical Maintenance',
          desc: 'Specialized technicians inspect every equipment before and after each rental: brakes, battery, frame, wheels and electrical components. Damaged equipment is immediately withdrawn from circulation.'
        },
        {
          icon: Shield,
          title: 'Civil Liability Insurance',
          desc: 'Orthocare Rent holds civil liability insurance covering third-party damages during the rental period. All equipment is CE-certified and complies with European standards.'
        },
        {
          icon: BadgeCheck,
          title: 'Certifications & Compliance',
          desc: 'Gestos Ágeis Unipessoal Lda (VAT: PT515948039) operates as a certified medical equipment rental company. CE-marked equipment, compliant with the European Medical Devices Directive.'
        }
      ],
      seal: 'Protocol in compliance with DGS standards',
      footer: 'Over 10,000 equipment delivered without incidents in Portugal since 2020.'
    }
  };

  const tr = t[lang] || t.pt;

  return (
    <section className="py-10 px-4 bg-white">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 text-xs font-bold px-3 py-1.5 rounded-full mb-3">
            <Shield className="w-4 h-4" />
            {tr.seal}
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">{tr.title}</h2>
          <p className="text-slate-600 text-sm">{tr.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {tr.points.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} className="flex gap-3 p-4 rounded-xl border border-slate-200 bg-slate-50/50">
                <div className="shrink-0 w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-green-700" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 text-sm mb-1">{p.title}</h3>
                  <p className="text-slate-600 text-xs leading-relaxed">{p.desc}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
          <CheckCircle className="w-4 h-4 text-green-600" />
          {tr.footer}
        </div>
      </div>
    </section>
  );
}